import { select, put, take, takeEvery } from "redux-saga/effects";
import keyBy from "lodash/keyBy";
import DOMPurify from "dompurify";
import {
  default as requestWithSubTenantIfNeeded,
  currentSubTenantSelector,
  currentTenantSelector,
} from "../requestWithSubTenantIfNeeded";
import * as actions from "@p81-common/shell-core-js/dist/redux/networksGet";
import {
  SOCKET_EVENT_DEPLOY_SUCCESS,
  SOCKET_EVENT_DEPLOY_FAILED,
  SOCKET_EVENT_DESTROY_SUCCESS,
  SOCKET_EVENT_DESTROY_FAILED,
  SOCKET_EVENT_DEPLOY_CUSTOM_SUCCESS,
  SOCKET_EVENT_DEPLOY_CUSTOM_FAILED,
  SOCKET_EVENT_LOCATION_ADD_SUCCESS,
  SOCKET_EVENT_LOCATION_ADD_FAILED,
  SOCKET_EVENT_LOCATION_CUSTOM_ADD_SUCCESS,
  SOCKET_EVENT_LOCATION_CUSTOM_ADD_FAILED,
  SOCKET_EVENT_LOCATION_REMOVE_SUCCESS,
  SOCKET_EVENT_LOCATION_REMOVE_FAILED,
  SOCKET_EVENT_SCALEUNIT_INCREASE_SUCCESS,
  SOCKET_EVENT_SCALEUNIT_INCREASE_FAILED,
  SOCKET_EVENT_SCALEUNIT_REDUCE_SUCCESS,
  SOCKET_EVENT_SCALEUNIT_REDUCE_FAILED,
  SOCKET_EVENT_NODE_ADD_SUCCESS,
  SOCKET_EVENT_NODE_ADD_FAILED,
  SOCKET_EVENT_NODE_CUSTOM_ADD_SUCCESS,
  SOCKET_EVENT_NODE_CUSTOM_ADD_FAILED,
  SOCKET_EVENT_NODE_REMOVE_SUCCESS,
  SOCKET_EVENT_NODE_REMOVE_FAILED,
  SOCKET_EVENT_NODE_CHANGE_STATE_SUCCESS,
  SOCKET_EVENT_NODE_CHANGE_STATE_FAILED,
  SOCKET_EVENT_REGION_CHANGE_STATE_SUCCESS,
  SOCKET_EVENT_REGION_CHANGE_STATE_FAILED,
  SOCKET_EVENT_SPLITTUNNELING_UPDATE_SUCCESS,
  SOCKET_EVENT_SPLITTUNNELING_UPDATE_FAILED,
  SOCKET_EVENT_CUSTOMDNS_UPDATE_SUCCESS,
  SOCKET_EVENT_CUSTOMDNS_UPDATE_FAILED,
  SOCKET_EVENT_TUNNEL_ADD_SUCCESS,
  SOCKET_EVENT_TUNNEL_ADD_FAILED,
  SOCKET_EVENT_TUNNEL_UPDATE_SUCCESS,
  SOCKET_EVENT_TUNNEL_UPDATE_FAILED,
  SOCKET_EVENT_TUNNEL_REMOVE_SUCCESS,
  SOCKET_EVENT_TUNNEL_REMOVE_FAILED,
  SOCKET_EVENT_TUNNEL_HA_ADD_SUCCESS,
  SOCKET_EVENT_TUNNEL_HA_ADD_FAILED,
  SOCKET_EVENT_TUNNEL_HA_UPDATE_SUCCESS,
  SOCKET_EVENT_TUNNEL_HA_UPDATE_FAILED,
  SOCKET_EVENT_TUNNEL_HA_REMOVE_SUCCESS,
  SOCKET_EVENT_TUNNEL_HA_REMOVE_FAILED,
  SOCKET_EVENT_DNSFILTERING_UPDATE_SUCCESS,
  SOCKET_EVENT_DNSFILTERING_UPDATE_FAILED,
} from "../networksBeaconsGet/networksBeaconsGetActions";
import { resources, hasAccess, arrayToMap, modifyNetworkDefaultIconUrl, hasFeatureFlag, ffKeys } from "@p81-common/shell-core-js";
import { splitTunnelingAddressesMap } from "../splitTunnelingAddressesMap";
import { normalizeV2NetworkListItem } from "./normalizeV2NetworkListItem";

// helper for activity logs for updating network names
export function* getNetworksSaga() {
  let networksGet = yield select((s) => s.networksGet);
  if (networksGet?.data) {
    return networksGet.data.networks;
  } else {
    if (!networksGet?.loading) {
      yield put(actions.request());
    }
    let result = yield take([
      actions.NETWORKS_GET_REQUEST_SUCCESS,
      actions.NETWORKS_GET_REQUEST_FAILURE,
    ]);
    if (result.type === actions.NETWORKS_GET_REQUEST_SUCCESS) {
      return result.data.networks;
    }
    return [];
  }
}

function* networksGet(action) {
  try {
    let tenantId = yield select(currentSubTenantSelector);

    if (!tenantId) {
      tenantId = yield select(currentTenantSelector);
    }
    if (
      action.type !== actions.NETWORKS_GET_REQUEST &&
      action.data.networkBeacon.tenantId !== tenantId
    ) {
      return;
    }
    let response = yield requestWithSubTenantIfNeeded("get", "networks");

    if (hasFeatureFlag(ffKeys.NETWORKS_LIST_BACKWARD_COMPATIBILITY)) {
      response.data.networks = response.data.networks.map(
        normalizeV2NetworkListItem
      );
    }

    let { networkFeatures } = response.data.relationships;
    let versions = keyBy(response.data.versions, "version");

    response.data.relationships.addresses = arrayToMap(
      "id",
      response.data.relationships.addresses || []
    );
    response.data.relationships.updatableObjects = arrayToMap(
      "cpId",
      response.data.relationships.updatableObjects || []
    );

    response.data.networks.forEach((network) => {
      network.name = DOMPurify.sanitize(network.name);
      network.tags = network.tags.map((tag) => {
        return DOMPurify.sanitize(tag);
      });

      modifyNetworkDefaultIconUrl(network);

      if (network.attributes.splitTunneling) {
        splitTunnelingAddressesMap(
          network,
          response.data.relationships
        );
      }

      let version = versions[network.meta.version];

      network.maxSubnetMaskLength = version.networkSubnetCidr;
      network.haTunnelsSupport = version.highAvailabilityTunnels;
      let featuresSupportedByNetwork = networkFeatures?.[network.id];
      if (featuresSupportedByNetwork) {
        network.supports = featuresSupportedByNetwork;
      }
    });

    response.data.defaultConfig = response.data.versions.find((v) => v.default);

    response.data.relationships.networkGroups.forEach((group) => {
      group.name = DOMPurify.sanitize(group.name);
    });

    if (hasAccess(resources.NETWORK_POLICY_ADDON)) {
      let { relationships, networks } = response.data;

      for (let network of networks) {
        network.networkPolicy = relationships.networkPolicies.find(
          (policy) => policy.network === network.id
        );
      }
    }

    yield put(actions.requestSuccess(response.data, tenantId));
  } catch (e) {
    yield put(actions.requestFailure(e));
  }
}

function* saga() {
  yield takeEvery(
    [
      actions.NETWORKS_GET_REQUEST,
      SOCKET_EVENT_DEPLOY_SUCCESS,
      SOCKET_EVENT_DEPLOY_FAILED,
      SOCKET_EVENT_DESTROY_SUCCESS,
      SOCKET_EVENT_DESTROY_FAILED,
      SOCKET_EVENT_DEPLOY_CUSTOM_SUCCESS,
      SOCKET_EVENT_DEPLOY_CUSTOM_FAILED,
      SOCKET_EVENT_LOCATION_ADD_SUCCESS,
      SOCKET_EVENT_LOCATION_ADD_FAILED,
      SOCKET_EVENT_LOCATION_CUSTOM_ADD_SUCCESS,
      SOCKET_EVENT_LOCATION_CUSTOM_ADD_FAILED,
      SOCKET_EVENT_LOCATION_REMOVE_SUCCESS,
      SOCKET_EVENT_LOCATION_REMOVE_FAILED,
      SOCKET_EVENT_SCALEUNIT_INCREASE_SUCCESS,
      SOCKET_EVENT_SCALEUNIT_INCREASE_FAILED,
      SOCKET_EVENT_SCALEUNIT_REDUCE_SUCCESS,
      SOCKET_EVENT_SCALEUNIT_REDUCE_FAILED,
      SOCKET_EVENT_NODE_ADD_SUCCESS,
      SOCKET_EVENT_NODE_ADD_FAILED,
      SOCKET_EVENT_NODE_CUSTOM_ADD_SUCCESS,
      SOCKET_EVENT_NODE_CUSTOM_ADD_FAILED,
      SOCKET_EVENT_NODE_REMOVE_SUCCESS,
      SOCKET_EVENT_NODE_REMOVE_FAILED,
      SOCKET_EVENT_NODE_CHANGE_STATE_SUCCESS,
      SOCKET_EVENT_NODE_CHANGE_STATE_FAILED,
      SOCKET_EVENT_REGION_CHANGE_STATE_SUCCESS,
      SOCKET_EVENT_REGION_CHANGE_STATE_FAILED,
      SOCKET_EVENT_SPLITTUNNELING_UPDATE_SUCCESS,
      SOCKET_EVENT_SPLITTUNNELING_UPDATE_FAILED,
      SOCKET_EVENT_CUSTOMDNS_UPDATE_SUCCESS,
      SOCKET_EVENT_CUSTOMDNS_UPDATE_FAILED,
      SOCKET_EVENT_TUNNEL_ADD_SUCCESS,
      SOCKET_EVENT_TUNNEL_ADD_FAILED,
      SOCKET_EVENT_TUNNEL_UPDATE_SUCCESS,
      SOCKET_EVENT_TUNNEL_UPDATE_FAILED,
      SOCKET_EVENT_TUNNEL_REMOVE_SUCCESS,
      SOCKET_EVENT_TUNNEL_REMOVE_FAILED,
      SOCKET_EVENT_TUNNEL_HA_ADD_SUCCESS,
      SOCKET_EVENT_TUNNEL_HA_ADD_FAILED,
      SOCKET_EVENT_TUNNEL_HA_UPDATE_SUCCESS,
      SOCKET_EVENT_TUNNEL_HA_UPDATE_FAILED,
      SOCKET_EVENT_TUNNEL_HA_REMOVE_SUCCESS,
      SOCKET_EVENT_TUNNEL_HA_REMOVE_FAILED,
      SOCKET_EVENT_DNSFILTERING_UPDATE_SUCCESS,
      SOCKET_EVENT_DNSFILTERING_UPDATE_FAILED,
    ],
    networksGet
  );
}

export default saga;
